import { useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import moment from 'moment/moment';
import { AxiosResponse } from 'axios';
import cn from "classnames";
import { Button, ButtonSize, ButtonType } from "@components/Button";
import { TabsList, TabsContent } from "@components/Tabs/Tabs";
import Page from "@components/Page";
import { useSellersCounter } from '@contexts/SellersCounterContext';
import CashbackTab from "./tabs/CashbackTab";
import OperationsTab from "./tabs/OperationsTab";
// import RefsTab from "./tabs/RefsTab";
import FilterIcon from "@assets/icons/filter.svg";
import AmountBlock from "./components/AmountBlock";
import {
  getUniClubParcels,
  getUniClubPaymentsTotal,
  getOperationsXLSX,
} from '@api/apiClient/club';
import { getErrorCode, isApiErrorWithResponse } from "@api/apiClient";
import useTab from "@hooks/useTab";
import styles from "./UniClubPage.module.scss";

const UniClubPage = () => {
  const { t } = useTranslation();
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const { sellersNotFound } = useSellersCounter();

  const { data: paymentTotal, refetch: paymentTotalRefetch } = useQuery({
    queryKey: ["getUniClubPaymentsTotal"],
    queryFn: getUniClubPaymentsTotal,
    retry: 1,
    select: ({ amount }) => (amount ?? { amount: 0, currency: "" }),
    enabled: !sellersNotFound,
  });

  const { error } = useQuery({
    queryKey: ["getUniClubParcels", 1, 10],
    queryFn: () => getUniClubParcels({ page: { page: 1, pageSize: 10 }, filters: [] }),
    retry: 1,
    enabled: !sellersNotFound,
  });

  const onUpdate = () => {
    paymentTotalRefetch();
  }

  const downloadingExcelFile = (): void => {
    getOperationsXLSX()
      .then((response: AxiosResponse) => {
        const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const fileURL = URL.createObjectURL(file);
        const element = document.createElement('a');
        element.setAttribute('href', fileURL);
        element.setAttribute('download', `${t('uniClub.operationsTab.title')} ${moment().format('DD/MM/YYYY HH:mm:ss')}`);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
      });
  };

  const tabs = [
    {
      id: "cashbackTab",
      title: t("uniClub.cashbackTab.title"),
      content: (
        <CashbackTab
          sellersNotFound={sellersNotFound}
          onUpdate={onUpdate}
        />
      ),
    },
    {
      id: "operationsTab",
      title: t("uniClub.operationsTab.title"),
      content: (
        <OperationsTab
          isFilterOpen={isFilterOpen}
          setIsFilterOpen={setIsFilterOpen}
          sellersNotFound={sellersNotFound}
          onUpdate={onUpdate}
        />
      ),
    },
    // {
    //   id: "refsTab",
    //   title: t("uniClub.refsTab.title"),
    //   content: (
    //     <RefsTab
    //       sellersNotFound={sellersNotFound}
    //       onUpdate={onUpdate}
    //     />
    //   ),
    // },
  ];
  const { activeTab, setActiveTab } = useTab(tabs);

  const isExternalError = isApiErrorWithResponse(error)
    && (getErrorCode(error, "") === "external-service-error");

  return (
    <Page title={t("uniClub.title")}>
      <div className={styles.tabsRow}>
        <TabsList
          tabs={tabs}
          onChange={setActiveTab}
          value={activeTab}
        />
        {!sellersNotFound && (
          <div className={styles.filters}>
            {((activeTab === "operationsTab") && !isExternalError) ? (
              <Button
                onClick={downloadingExcelFile}
                size={ButtonSize.S}
                type={ButtonType.SKELETON}
                title={t("uniClub.downoadBtn")}
              />
            ) : null}

            {((activeTab === "operationsTab") && !isExternalError) ? (
              <Button
                className={cn(isFilterOpen ? styles.filterBtnActive : styles.filterBtnInactive)}
                icon={FilterIcon}
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                size={ButtonSize.S}
                type={ButtonType.ROUNDED}
                title={t("filters.filters")}
              />
            ) : null}

            {(activeTab !== "refsTab") ? (
              <AmountBlock
                value={paymentTotal?.amount ?? 0}
                label={t("uniClub.earned")}
                currency={paymentTotal?.currency ?? ""}
                align="right"
              />
            ) : null}
          </div>
        )}
      </div>

      <TabsContent
        tab={tabs.find((tab) => (tab.id === activeTab))}
      />
    </Page>
  );
};

export default UniClubPage;
